/**
 * Props for {@link QualityDiamonds}.
 */
export interface QualityDiamondsProps {
  /** Number of lit diamonds (1..5). Values outside the range are clamped. */
  quality: number;
  /**
   * Override for the default accessible label.
   * Defaults to `Qualité {quality} sur 5`.
   */
  ariaLabel?: string;
}

const TOTAL = 5;

/**
 * Five 6×6 rotated squares ("diamonds") used to render the MIDI analysis
 * confidence score on a 1..5 scale. Lit diamonds glow with the accent token,
 * unlit ones stay at a low-contrast white.
 *
 * Pure presentational. No interaction, no hover state.
 */
export function QualityDiamonds({ quality, ariaLabel }: QualityDiamondsProps) {
  const clamped = Math.max(0, Math.min(TOTAL, Math.round(quality)));
  const label = ariaLabel ?? `Qualité ${clamped} sur 5`;

  return (
    <span
      role="img"
      aria-label={label}
      className="inline-flex items-center gap-[3px]"
    >
      {Array.from({ length: TOTAL }, (_, i) => {
        const lit = i < clamped;

        return (
          <span
            key={i}
            aria-hidden
            className="inline-block"
            style={{
              width: 6,
              height: 6,
              transform: "rotate(45deg)",
              backgroundColor: lit
                ? "var(--color-accent-bright)"
                : "rgba(255, 255, 255, 0.10)",
              boxShadow: lit ? "0 0 4px rgba(167, 139, 250, 0.35)" : "none",
            }}
          />
        );
      })}
    </span>
  );
}
